import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import supabase from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiHeart,
  FiUser,
  FiMail,
  FiPhone,
  FiClock,
  FiRefreshCw,
  FiTrash2,
  FiCheckCircle,
  FiStar,
} = FiIcons;

const statusOptions = [
  { value: 'new', label: 'Νέο' },
  { value: 'contacted', label: 'Επικοινωνήσαμε' },
  { value: 'converted', label: 'Έγινε Χορηγός' },
  { value: 'closed', label: 'Κλειστό' },
];

const statusStyles = {
  new: 'bg-blue-50 text-blue-700 border-blue-100',
  contacted: 'bg-amber-50 text-amber-700 border-amber-100',
  converted: 'bg-green-50 text-green-700 border-green-100',
  closed: 'bg-gray-100 text-gray-600 border-gray-200',
};

const SponsorshipRequestsAdmin = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [scopeFilter, setScopeFilter] = useState('all');

  const fetchRequests = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const { data, error } = await supabase
        .from('sponsorship_requests_aox1234')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (err) {
      setMessage({ type: 'error', text: `Αποτυχία φόρτωσης ενδιαφέροντος: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const filteredRequests = useMemo(() => {
    if (scopeFilter === 'all') return requests;
    return requests.filter((request) => request.request_scope === scopeFilter);
  }, [requests, scopeFilter]);

  const updateStatus = async (id, status) => {
    try {
      const { error } = await supabase
        .from('sponsorship_requests_aox1234')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      setRequests((prev) => prev.map((item) => (item.id === id ? { ...item, status } : item)));
    } catch (err) {
      setMessage({ type: 'error', text: `Δεν άλλαξε η κατάσταση: ${err.message}` });
    }
  };

  const deleteRequest = async (request) => {
    if (!window.confirm(`Να διαγραφεί το ενδιαφέρον από ${request.name};`)) return;
    try {
      const { error } = await supabase
        .from('sponsorship_requests_aox1234')
        .delete()
        .eq('id', request.id);
      if (error) throw error;
      setRequests((prev) => prev.filter((item) => item.id !== request.id));
    } catch (err) {
      setMessage({ type: 'error', text: `Αποτυχία διαγραφής: ${err.message}` });
    }
  };

  const formatDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('el-GR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const newCount = requests.filter((request) => request.status === 'new').length;
  const playerCount = requests.filter((request) => request.request_scope === 'player').length;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-primary-100 bg-primary-50 p-5">
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary-500">Σύνολο</p>
          <p className="mt-2 text-3xl font-black text-primary-900">{requests.length}</p>
          <p className="mt-1 text-sm text-primary-700">ενδιαφέροντα χορηγίας</p>
        </div>
        <div className="rounded-2xl border border-blue-100 bg-white p-5">
          <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-500">Νέα</p>
          <p className="mt-2 text-3xl font-black text-gray-900">{newCount}</p>
          <p className="mt-1 text-sm text-gray-500">περιμένουν επικοινωνία</p>
        </div>
        <div className="rounded-2xl border border-yellow-100 bg-yellow-50 p-5">
          <p className="text-xs font-black uppercase tracking-[0.16em] text-yellow-700">Για παίκτριες</p>
          <p className="mt-2 text-3xl font-black text-yellow-950">{playerCount}</p>
          <p className="mt-1 text-sm text-yellow-800">player-specific requests</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <div className="mr-auto">
          <h2 className="font-black text-gray-900">Ενδιαφέρον Χορηγίας</h2>
          <p className="text-xs text-gray-500">Name · email · phone, όπως ακριβώς ζητήθηκε.</p>
        </div>
        <select
          value={scopeFilter}
          onChange={(event) => setScopeFilter(event.target.value)}
          className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm font-semibold outline-none"
        >
          <option value="all">Όλα</option>
          <option value="club">Ομάδα</option>
          <option value="player">Παίκτρια</option>
        </select>
        <button
          type="button"
          onClick={fetchRequests}
          className="inline-flex items-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50"
        >
          <SafeIcon icon={FiRefreshCw} className="mr-2 h-4 w-4" />
          Ανανέωση
        </button>
      </div>

      {message.text && (
        <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm font-semibold text-red-700">
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-gray-500">Φόρτωση...</div>
      ) : filteredRequests.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white py-16 text-center">
          <SafeIcon icon={FiHeart} className="mx-auto mb-3 h-10 w-10 text-gray-300" />
          <p className="font-bold text-gray-600">Δεν υπάρχουν ακόμη αιτήματα σε αυτή την κατηγορία.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {filteredRequests.map((request) => (
            <motion.div
              key={request.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-black ${
                        request.request_scope === 'player'
                          ? 'border-yellow-200 bg-yellow-50 text-yellow-800'
                          : 'border-primary-100 bg-primary-50 text-primary-700'
                      }`}
                    >
                      <SafeIcon icon={request.request_scope === 'player' ? FiStar : FiHeart} className="mr-1 h-3 w-3" />
                      {request.request_scope === 'player' ? 'ΠΑΙΚΤΡΙΑ' : 'ΟΜΑΔΑ'}
                    </span>
                    <span className={`rounded-full border px-2.5 py-1 text-[11px] font-black ${statusStyles[request.status] || statusStyles.new}`}>
                      {statusOptions.find((option) => option.value === request.status)?.label || request.status}
                    </span>
                  </div>
                  <h3 className="text-lg font-black text-gray-900">{request.name}</h3>
                  {request.request_scope === 'player' && request.player_name && (
                    <p className="mt-1 text-sm font-bold text-primary-700">Για: {request.player_name}</p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => deleteRequest(request)}
                  className="rounded-lg p-2 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                  title="Διαγραφή"
                >
                  <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-5 grid gap-2 text-sm">
                <a href={`mailto:${request.email}`} className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2.5 font-semibold text-gray-700 hover:bg-primary-50 hover:text-primary-700">
                  <SafeIcon icon={FiMail} className="h-4 w-4" />
                  {request.email}
                </a>
                <a href={`tel:${request.phone}`} className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2.5 font-semibold text-gray-700 hover:bg-primary-50 hover:text-primary-700">
                  <SafeIcon icon={FiPhone} className="h-4 w-4" />
                  {request.phone}
                </a>
                <div className="flex items-center gap-2 px-1 pt-1 text-xs text-gray-400">
                  <SafeIcon icon={FiClock} className="h-3.5 w-3.5" />
                  {formatDate(request.created_at)}
                  {request.source_location ? ` · ${request.source_location}` : ''}
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
                <span className="mr-1 text-xs font-bold text-gray-500">Κατάσταση:</span>
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => updateStatus(request.id, option.value)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                      request.status === option.value
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {option.value === 'converted' && <SafeIcon icon={FiCheckCircle} className="mr-1 inline h-3 w-3" />}
                    {option.label}
                  </button>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SponsorshipRequestsAdmin;